import { useEffect, useState, type FormEvent } from "react";
import { api, unwrap } from "../api/client";
import type { InputView, OutputProtocol, OutputView } from "../api/types";
import { useClientLimits } from "../lib/auth";
import { N_, t } from "../lib/i18n";
import { OwnerSelect } from "./OwnerSelect";
import { Button, Checkbox, Field, FormError, Modal, Segmented, Select, TextInput } from "./ui";

type UrlProtocol = Exclude<OutputProtocol, "srt_listener" | "ndi">;

const URL_PLACEHOLDER: Record<UrlProtocol, string> = {
  srt: N_("srt://cdn.exemple.com:9000?mode=caller&latency=200000"),
  rtmp: "rtmp://a.rtmp.youtube.com/live2/xxxx-xxxx-xxxx",
  udp: "udp://239.1.1.1:5000?ttl=4",
  rtsp: N_("rtsp://serveur:8554/chemin"),
};
const URL_HINT: Record<UrlProtocol, string> = {
  srt: N_("La latence SRT est en microsecondes : latency=200000 pour 200 ms."),
  rtmp: N_("RTMP transporte du H.264 + AAC, sur une piste : avec une source AC3 ou multipiste, choisissez AAC stéréo et une piste ci-dessous."),
  udp: N_("MPEG-TS en UDP. Pour du multicast, ajoutez ?ttl=4 (nombre de routeurs traversés)."),
  rtsp: N_("Publication RTSP en TCP vers un autre serveur."),
};

/** Pistes audio de la sortie : toutes, ou une sélection dans celles de la source (une seule en NDI). */
function AudioTracks({ labels, value, single, onChange }: {
  labels: string[]; value: number[] | null; single: boolean; onChange: (v: number[] | null) => void;
}) {
  const count = Math.max(labels.length, ...(value ?? []).map((n) => n + 1));
  // texte en cours de saisie (source hors ligne) : recalculé à chaque frappe, il perdrait la virgule de « 1, »
  const [typed, setTyped] = useState((value ?? [0]).map((n) => n + 1).join(", "));
  const toggle = (track: number, on: boolean) => {
    if (single) return onChange([track]);
    const next = on ? [...(value ?? []), track].sort((a, b) => a - b) : (value ?? []).filter((v) => v !== track);
    onChange(next.length ? next : [track]);          // au moins une : sinon, c'est « Toutes »
  };
  return (
    <Field label={t("Pistes audio")} hint={labels.length ? undefined : t("Source hors ligne : numéros des pistes, séparés par des virgules (1, 3…).")}>
      <Segmented label={t("Pistes audio")} value={value ? "some" : "all"} onChange={(v) => onChange(v === "all" ? null : [0])}
        options={[{ value: "all", label: single ? t("La première") : t("Toutes") }, { value: "some", label: single ? t("Choisir") : t("Sélection") }]} />
      {value && (labels.length > 0 ? (
        <div className="mt-2 flex flex-col gap-1">
          {Array.from({ length: count }, (_, track) => (
            <Checkbox key={track} id={`out-track-${track}`}
              label={labels[track] ? t("Piste {n} · {label}", { n: track + 1, label: labels[track]! }) : t("Piste {n}", { n: track + 1 })}
              checked={value.includes(track)} onChange={(on) => toggle(track, on)} />
          ))}
        </div>
      ) : (
        <TextInput className="mt-2" value={typed} placeholder="1, 3" aria-label={t("Numéros des pistes")}
          onChange={(e) => {
            setTyped(e.target.value);
            const tracks = e.target.value.split(",").map((n) => n.trim()).filter(Boolean).map((n) => Number(n) - 1)
              .filter((n) => Number.isInteger(n) && n >= 0);
            onChange(single ? tracks.slice(0, 1) : tracks.length ? tracks : [0]);
          }} />
      ))}
    </Field>
  );
}

type Draft = Omit<OutputView, "id" | "status" | "listener" | "overlay_state" | "overlay_has_logo">;

function draftFrom(output: OutputView | undefined, inputs: InputView[]): Draft {
  return {
    name: output?.name ?? "", input_id: output?.input_id ?? inputs[0]?.id ?? "",
    backup_input_id: output?.backup_input_id ?? null, protocol: output?.protocol ?? "srt",
    url: output?.url ?? "", listener_key: output?.listener_key ?? null, srt_passphrase: output?.srt_passphrase ?? null,
    enabled: output?.enabled ?? true, video: output?.video ?? "copy",
    video_bitrate_kbps: output?.video_bitrate_kbps ?? 4000, audio: output?.audio ?? "copy",
    audio_bitrate_kbps: output?.audio_bitrate_kbps ?? 128, overlay: output?.overlay ?? false,
    audio_tracks: output?.audio_tracks ?? null, ndi_name: output?.ndi_name ?? null, ndi_format: output?.ndi_format ?? "1080p50",
    owner: output?.owner ?? null,
  };
}

export function OutputForm({ output, inputs, onClose, onSaved }: {
  output?: OutputView; inputs: InputView[]; onClose: () => void; onSaved: (saved: OutputView) => void;
}) {
  const editing = Boolean(output);
  const limits = useClientLimits();          // compte client : options limitées (le serveur vérifie aussi)
  const canTranscode = !limits || limits.transcode;
  const [d, setD] = useState<Draft>(() => draftFrom(output, inputs));
  // une sortie d'un client ne part que des entrées de ce client
  const sources = d.owner ? inputs.filter((i) => i.owner === d.owner) : inputs;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((prev) => ({ ...prev, [k]: v }));
  const listener = d.protocol === "srt_listener";
  const toNdi = d.protocol === "ndi";
  const audioLabels = inputs.find((i) => i.id === d.input_id)?.live.audio_tracks ?? [];
  const audioTracks = d.audio !== "none" && (
    <AudioTracks labels={audioLabels} value={d.audio_tracks ?? null} single={toNdi} onChange={(v) => set("audio_tracks", v)} />
  );
  // l'option NDI n'existe que si la bibliothèque est sur le serveur ; toujours visible sur une sortie déjà NDI
  const [ndiAvailable, setNdiAvailable] = useState(false);
  useEffect(() => {
    if (limits) return;                      // le NDI n'est pas proposé aux comptes clients
    unwrap(api.GET("/api/ndi")).then((n) => setNdiAvailable(n.available)).catch(() => setNdiAvailable(false));
  }, [limits]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = { ...d, name: d.name.trim(), url: listener || toNdi ? "" : d.url.trim(), srt_passphrase: d.srt_passphrase || null, overlay_logo_rev: 0, ndi_rev: 0 };   // numéro du logo : tenu par le serveur
    try {
      const saved = editing
        ? await unwrap(api.PUT("/api/outputs/{output_id}", { params: { path: { output_id: output!.id } }, body }))
        : await unwrap(api.POST("/api/outputs", { body }));
      onSaved(saved);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={editing ? t("Modifier « {name} »", { name: output!.name }) : t("Nouvelle sortie")} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t("Annuler")}</Button>
        <Button variant="primary" type="submit" form="output-form" disabled={busy || inputs.length === 0}>
          {editing ? t("Enregistrer et relancer") : t("Créer la sortie")}
        </Button>
      </>}>
      <form id="output-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t("Nom")} htmlFor="out-name">
          <TextInput id="out-name" required maxLength={80} value={d.name} onChange={(e) => set("name", e.target.value)}
            placeholder={t("Vers CDN")} autoFocus />
        </Field>
        <OwnerSelect id="out-owner" value={d.owner} onChange={(owner) => setD((prev) => {
          const own = owner ? inputs.filter((i) => i.owner === owner) : inputs;
          const keep = own.some((i) => i.id === prev.input_id);
          return { ...prev, owner, input_id: keep ? prev.input_id : own[0]?.id ?? "",
            backup_input_id: own.some((i) => i.id === prev.backup_input_id) ? prev.backup_input_id : null };
        })} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Source")} htmlFor="out-src">
            <Select id="out-src" required value={d.input_id} onChange={(e) => {
              const id = e.target.value;
              setD((prev) => ({ ...prev, input_id: id, backup_input_id: prev.backup_input_id === id ? null : prev.backup_input_id }));
            }}>
              {sources.length === 0 && <option value="">{t("Créez d'abord une entrée")}</option>}
              {sources.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.id})</option>)}
            </Select>
          </Field>
          <Field label={t("Secours")} htmlFor="out-backup">
            <Select id="out-backup" value={d.backup_input_id ?? ""} onChange={(e) => set("backup_input_id", e.target.value || null)}>
              <option value="">{t("Aucun")}</option>
              {sources.filter((i) => i.id !== d.input_id).map((i) => <option key={i.id} value={i.id}>{i.name} ({i.id})</option>)}
            </Select>
          </Field>
        </div>
        {d.backup_input_id && (
          <p className="-mt-2 text-[12px] text-faint">
            {t("Si la source est perdue plus de quelques secondes, la sortie bascule sur le secours, puis revient sur la source quand elle est stable. Chaque bascule coupe la destination 1 à 2 s.")}
          </p>
        )}
        <Field label={t("Protocole de destination")} htmlFor="out-proto">
          <Select id="out-proto" value={d.protocol} onChange={(e) => {
            const protocol = e.target.value as OutputProtocol;
            // l'habillage demande le transcodage, sauf en NDI : ne pas garder une option devenue invisible
            setD((prev) => ({
              ...prev, protocol, overlay: prev.overlay && (protocol === "ndi" || prev.video === "h264"),
              audio_tracks: protocol === "ndi" ? prev.audio_tracks?.slice(0, 1) ?? null : prev.audio_tracks,   // NDI : une piste
            }));
          }}>
            <option value="srt">{t("SRT (la gateway se connecte à la destination)")}</option>
            <option value="srt_listener">{t("SRT listener (le client se connecte à la gateway)")}</option>
            {(ndiAvailable || toNdi) && <option value="ndi">{t("NDI (source sur le réseau local)")}</option>}
            <option value="rtmp">{t("RTMP / RTMPS (YouTube, Facebook, Twitch…)")}</option>
            {(!limits || d.protocol === "udp") && <option value="udp">UDP MPEG-TS</option>}
            <option value="rtsp">RTSP</option>
          </Select>
        </Field>
        {toNdi ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("Nom de la source NDI")} htmlFor="out-ndi-name" hint={t("Vide : le nom de la sortie.")}>
              <TextInput id="out-ndi-name" maxLength={64} value={d.ndi_name ?? ""}
                onChange={(e) => set("ndi_name", e.target.value || null)} placeholder={d.name} />
            </Field>
            <Field label={t("Format envoyé")} htmlFor="out-ndi-format" hint={t("La source est mise à ce format ; le 1080p50 demande le plus de processeur.")}>
              <Select id="out-ndi-format" value={d.ndi_format} onChange={(e) => set("ndi_format", e.target.value as Draft["ndi_format"])}>
                <option value="1080p50">1080p50</option>
                <option value="1080p25">1080p25</option>
                <option value="720p50">720p50</option>
                <option value="720p25">720p25</option>
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Checkbox id="out-ndi-audio" label={t("Envoyer le son (à décocher si la source n'a pas de piste audio)")}
                checked={d.audio !== "none"} onChange={(v) => set("audio", v ? "copy" : "none")} />
            </div>
            <div className="sm:col-span-2">{audioTracks}</div>
          </div>
        ) : listener ? (
          <>
            <p className="-mt-2 text-[12px] text-faint">
              {t("Le client règle son décodeur en mode caller vers le port SRT de la gateway, avec le streamid de cette sortie : il contient une clé propre à la sortie, que vous pourrez changer à tout moment. Ces réglages s'affichent dans « Accès client », sur la page des sorties.")}
            </p>
            <Field label={t("Passphrase SRT (facultative)")} htmlFor="out-pass"
              hint={t("Chiffre la liaison (AES) : le client doit la saisir. 10 à 79 caractères ; vide = pas de chiffrement.")}>
              <TextInput id="out-pass" minLength={10} maxLength={79} value={d.srt_passphrase ?? ""}
                onChange={(e) => set("srt_passphrase", e.target.value || null)} className="font-mono text-[13px]"
                autoComplete="off" />
            </Field>
          </>
        ) : (
          <Field label={t("URL de destination")} htmlFor="out-url" hint={t(URL_HINT[d.protocol as UrlProtocol])}>
            <TextInput id="out-url" required value={d.url} onChange={(e) => set("url", e.target.value)}
              placeholder={t(URL_PLACEHOLDER[d.protocol as UrlProtocol])} className="font-mono text-[13px]" />
          </Field>
        )}
        {!toNdi && <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("Vidéo")} hint={!canTranscode ? undefined : d.video === "copy" ? t("Sans transcodage : presque aucun CPU.") : t("Transcodage H.264 : coûteux en CPU.")}>
            <Segmented label={t("Vidéo")} value={d.video} onChange={(v) => setD((prev) => ({ ...prev, video: v, overlay: v === "h264" && prev.overlay }))}
              options={canTranscode || d.video === "h264" ? [{ value: "copy", label: t("Copie") }, { value: "h264", label: "H.264" }]
                : [{ value: "copy", label: t("Copie") }]} />
            {!canTranscode && <p className="mt-1 text-[12px] text-faint">{t("Transcodage non inclus dans votre compte.")}</p>}
          </Field>
          {d.video === "h264" && (
            <Field label={t("Débit vidéo (kb/s)")} htmlFor="out-vbr">
              <TextInput id="out-vbr" type="number" min={100} max={100000} step={100} value={d.video_bitrate_kbps}
                onChange={(e) => set("video_bitrate_kbps", Number(e.target.value))} />
            </Field>
          )}
          <Field label={t("Audio")}>
            <Segmented label={t("Audio")} value={d.audio} onChange={(v) => set("audio", v)}
              options={[{ value: "copy", label: t("Copie") }, { value: "aac", label: t("AAC stéréo") }, { value: "none", label: t("Aucun") }]} />
          </Field>
          {d.audio === "aac" && (
            <Field label={t("Débit audio (kb/s)")} htmlFor="out-abr">
              <TextInput id="out-abr" type="number" min={32} max={512} step={16} value={d.audio_bitrate_kbps}
                onChange={(e) => set("audio_bitrate_kbps", Number(e.target.value))} />
            </Field>
          )}
          {d.audio !== "none" && <div className="sm:col-span-2">{audioTracks}</div>}
        </div>}
        {(d.video === "h264" || toNdi) && (
          <div>
            <Checkbox id="out-overlay" label={t("Habillage en direct (logo et texte)")} checked={d.overlay}
              onChange={(v) => set("overlay", v)} />
            <p className="mt-1 text-[12px] text-faint">
              {t("Réglé ensuite depuis le bouton « Habillage » de la sortie, sans la relancer. Pour une source entrelacée, activez d'abord la conversion progressive de l'entrée.")}
            </p>
          </div>
        )}
        <Checkbox id="out-enabled" label={t("Démarrer la sortie (et la relancer au redémarrage du serveur)")}
          checked={d.enabled} onChange={(v) => set("enabled", v)} />
        <FormError message={error} />
      </form>
    </Modal>
  );
}
